import type { AppContext, ViewCleanup } from "../app";
import {
  loadCountries,
  groupByRegion,
  type CountryMetadata,
} from "../data/countries";
import { loadState, saveState } from "../state/storage";
import { recordCorrect, recordWrong } from "../state/stats";
import { getArcadeBest, recordArcadeScore } from "../state/arcade";
import { pushLeaderboardBests } from "../state/sync";
import {
  startRound,
  drawNext,
  returnMissed,
  type RoundState,
} from "../lib/selection";
import { pickDistractors } from "../lib/distractors";
import { getCountriesForRegion } from "./region-picker";

const RUN_MS = 60_000;
const MC_CHOICE_COUNT = 4;
const ADVANCE_DELAY_MS = 350;

export const mountArcadeSpeedRun = async (
  root: HTMLElement,
  ctx: AppContext,
): Promise<ViewCleanup> => {
  const region = root.dataset.region;
  if (!region) {
    ctx.nav.goArcade();
    return () => {};
  }

  root.classList.add("view-arcade-run");
  ctx.globe.setHoverEnabled(false);
  ctx.globe.setClickHandler(null);

  const table = await loadCountries();
  const grouped = groupByRegion(table);
  let appState = loadState();
  const pool = getCountriesForRegion(region, grouped, appState);
  const poolIsos = pool.map((c) => c.iso);

  root.innerHTML = `
    <button type="button" class="arcade-run-back" aria-label="Back">← Back</button>
    <div class="arcade-run-hud">
      <span class="arcade-run-timer">60</span>
      <span class="arcade-run-divider">·</span>
      <span class="arcade-run-correct">0</span> ✓
    </div>
    <section class="arcade-run-panel">
      <p class="arcade-run-prompt">Pick the country</p>
      <div class="arcade-run-choices" role="group" aria-label="Choices"></div>
    </section>
    <section class="arcade-run-end" hidden>
      <h1 class="arcade-run-final-title">Time's up</h1>
      <p class="arcade-run-final-score"></p>
      <p class="arcade-run-final-best"></p>
      <div class="arcade-run-actions">
        <button type="button" class="arcade-run-retry">Run it again</button>
        <button type="button" class="arcade-run-leaderboard">🏆 Leaderboard</button>
        <button type="button" class="arcade-run-exit">Back to Arcade</button>
      </div>
    </section>
  `;

  const backBtn = root.querySelector(".arcade-run-back") as HTMLButtonElement;
  const timerEl = root.querySelector(".arcade-run-timer") as HTMLSpanElement;
  const correctEl = root.querySelector(".arcade-run-correct") as HTMLSpanElement;
  const promptEl = root.querySelector(".arcade-run-prompt") as HTMLParagraphElement;
  const choicesEl = root.querySelector(".arcade-run-choices") as HTMLDivElement;
  const panel = root.querySelector(".arcade-run-panel") as HTMLElement;
  const endEl = root.querySelector(".arcade-run-end") as HTMLElement;
  const finalScore = root.querySelector(".arcade-run-final-score") as HTMLParagraphElement;
  const finalBest = root.querySelector(".arcade-run-final-best") as HTMLParagraphElement;
  const retryBtn = root.querySelector(".arcade-run-retry") as HTMLButtonElement;
  const leaderboardBtn = root.querySelector(".arcade-run-leaderboard") as HTMLButtonElement;
  const exitBtn = root.querySelector(".arcade-run-exit") as HTMLButtonElement;

  let current: CountryMetadata | null = null;
  let correctCount = 0;
  let startedAt = 0;
  let tickHandle: number | null = null;
  let advanceHandle: number | null = null;
  let destroyed = false;
  let ended = false;
  let round: RoundState = startRound(poolIsos, Math.random);
  const listeners: Array<() => void> = [];

  if (pool.length < MC_CHOICE_COUNT) {
    promptEl.textContent = `Need at least ${MC_CHOICE_COUNT} countries to play. "${region}" has ${pool.length}.`;
    choicesEl.hidden = true;
    return () => {
      ctx.globe.setHoverEnabled(true);
      root.classList.remove("view-arcade-run");
    };
  }

  const renderChoices = (target: CountryMetadata, all: CountryMetadata[]): void => {
    choicesEl.replaceChildren();
    for (const c of all) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "arcade-run-choice";
      btn.textContent = c.canonicalName;
      const onPick = (): void => onAnswer(c, btn, target);
      btn.addEventListener("click", onPick);
      listeners.push(() => btn.removeEventListener("click", onPick));
      choicesEl.appendChild(btn);
    }
  };

  const nextQuestion = (): void => {
    if (destroyed || ended) return;
    const draw = drawNext(round, Math.random);
    round = draw.state;
    const pickedIso = draw.iso;
    if (!pickedIso) return;
    const picked = table[pickedIso];
    if (!picked) return;
    current = picked;
    const distractors = pickDistractors({
      target: picked,
      pool,
      count: MC_CHOICE_COUNT - 1,
      rng: Math.random,
    });
    const choices = [picked, ...distractors];
    for (let i = choices.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      const t = choices[i]!;
      choices[i] = choices[j]!;
      choices[j] = t;
    }
    renderChoices(picked, choices);
    ctx.globe.setTargetIso(picked.iso);
    void ctx.globe.flyToIso(picked.iso, { durationMs: 500 });
  };

  const onAnswer = (
    pick: CountryMetadata,
    btn: HTMLButtonElement,
    target: CountryMetadata,
  ): void => {
    if (ended || !current || current.iso !== target.iso) return;
    const correct = pick.iso === target.iso;
    btn.classList.add(correct ? "is-correct" : "is-wrong");
    const now = Date.now();
    if (correct) {
      correctCount += 1;
      correctEl.textContent = String(correctCount);
      appState = recordCorrect(appState, target.iso, now);
    } else {
      appState = recordWrong(appState, target.iso, now);
      round = returnMissed(round, target.iso, Math.random);
    }
    saveState(appState);
    // Disable all choice buttons during the brief reveal window.
    for (const b of choicesEl.querySelectorAll("button"))
      (b as HTMLButtonElement).disabled = true;
    advanceHandle = window.setTimeout(() => {
      advanceHandle = null;
      nextQuestion();
    }, ADVANCE_DELAY_MS);
  };

  const endRun = (): void => {
    if (ended) return;
    ended = true;
    if (tickHandle !== null) {
      window.clearInterval(tickHandle);
      tickHandle = null;
    }
    if (advanceHandle !== null) {
      window.clearTimeout(advanceHandle);
      advanceHandle = null;
    }
    panel.hidden = true;
    endEl.hidden = false;
    finalScore.textContent = `${correctCount} correct`;
    const prevBest = getArcadeBest(appState, "speed-run", region);
    const result = recordArcadeScore(
      appState,
      "speed-run",
      region,
      correctCount,
      Date.now(),
    );
    appState = result.state;
    saveState(appState);
    if (result.isNewBest) {
      finalBest.innerHTML = `<strong>New best!</strong> (was ${prevBest ? prevBest.score : 0})`;
      void pushLeaderboardBests(appState);
    } else {
      finalBest.textContent = `Best: ${prevBest ? prevBest.score : 0}`;
    }
  };

  const tick = (): void => {
    if (destroyed || ended) return;
    const remaining = Math.max(0, RUN_MS - (Date.now() - startedAt));
    timerEl.textContent = String(Math.ceil(remaining / 1000));
    if (remaining <= 0) endRun();
  };

  const onBack = (): void => {
    ctx.nav.goArcade();
  };
  const onRetry = (): void => {
    ctx.nav.goArcadeMode({ mode: "speed-run", region });
  };
  const onExit = (): void => {
    ctx.nav.goArcade();
  };
  const onLeaderboard = (): void => {
    ctx.nav.goLeaderboard({ mode: "speed-run", region });
  };
  backBtn.addEventListener("click", onBack);
  retryBtn.addEventListener("click", onRetry);
  leaderboardBtn.addEventListener("click", onLeaderboard);
  exitBtn.addEventListener("click", onExit);

  // Kick off
  startedAt = Date.now();
  tickHandle = window.setInterval(tick, 250);
  nextQuestion();

  return () => {
    destroyed = true;
    if (tickHandle !== null) window.clearInterval(tickHandle);
    if (advanceHandle !== null) window.clearTimeout(advanceHandle);
    for (const off of listeners) off();
    backBtn.removeEventListener("click", onBack);
    retryBtn.removeEventListener("click", onRetry);
    leaderboardBtn.removeEventListener("click", onLeaderboard);
    exitBtn.removeEventListener("click", onExit);
    ctx.globe.setTargetIso(null);
    ctx.globe.setHoverEnabled(true);
    root.classList.remove("view-arcade-run");
  };
};
